import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import {
    CardType,
    PaymentOptions,
} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {JobForm} from 'components/customer/Job/entity/JobForm';

interface Confirmation {
    removedBenchIds: number[];
    removedJobCabinetIds?: number[];
    totalGSTRemovedWithVariationCost: number;
    totalRemovedVariationCost: number;
    removedExtraVariationIds?: number[];

    defaultPaymentMethod?: PaymentMethodType;
    paymentMethod?: PaymentMethodType;
    paymentType?: PaymentType;

    gatwayAvailable?: boolean;
    invoiceAvailable?: boolean;
    accountPayAvailable?: boolean;

    isLoadingPaymentInfo?: boolean;
    isCardVerified?: boolean;
    errors: string[];

    paymentOptions?: PaymentOptions;
    cardType?: CardType;

    clientSecret?: string;

    paymentIntentId?: string;
    confirmationToken?: string;
    connectedAccountId?: string;

    updatingSession?: boolean;

    newJobTotal?: number;
    amountPaid?: number;
    isLoadingMisc?: boolean;
    overPaid?: boolean;

    fullAmount?: number;

    processingPayment?: boolean;

    previousPaymentMethod?: string;
    jobDetails?: JobForm;
}

const initialState: Confirmation = {
    errors: [],
    removedBenchIds: [],
    removedJobCabinetIds: [],
    removedExtraVariationIds: [],
    totalRemovedVariationCost: 0,
    totalGSTRemovedWithVariationCost: 0,
    isLoadingPaymentInfo: false,
    isCardVerified: false,
    updatingSession: false,

    gatwayAvailable: false,
    invoiceAvailable: false,
    accountPayAvailable: false,

    newJobTotal: 0,
    amountPaid: 0,
    isLoadingMisc: true,

    fullAmount: 0,

    processingPayment: false,
};

const confirmationSlice = createSlice({
    name: 'confirmation',
    initialState,
    reducers: {
        jobDetailsSet: (state, {payload}: PayloadAction<JobForm>) => {
            state.jobDetails = payload;
        },
        previousPaymentMethodSet: (state, {payload}: PayloadAction<string>) => {
            state.previousPaymentMethod = payload;
        },
        processingPaymentSet: (state, {payload}: PayloadAction<boolean>) => {
            state.processingPayment = payload;
        },
        fullAmountSet: (state, {payload}: PayloadAction<number>) => {
            state.fullAmount = payload;
        },
        isLoadingMiscSet: (state, {payload}: PayloadAction<boolean>) => {
            state.isLoadingMisc = payload;
        },
        miscDataSet: (
            state,
            {
                payload,
            }: PayloadAction<{
                newJobTotal: number;
                amountPaid: number;
                overPaid: boolean;
            }>
        ) => {
            state.newJobTotal = payload.newJobTotal;
            state.amountPaid = payload.amountPaid;
            state.overPaid = payload.overPaid;
        },
        removedVariationGSTSet: (state, {payload}: PayloadAction<number>) => {
            state.totalGSTRemovedWithVariationCost += payload;
        },
        accountPayAvailableSet: (state, {payload}: PayloadAction<boolean>) => {
            state.accountPayAvailable = payload;
        },
        updatingSessionSet: (state, {payload}: PayloadAction<boolean>) => {
            state.updatingSession = payload;
        },
        clientSecretSet: (state, {payload}: PayloadAction<string>) => {
            state.clientSecret = payload;
        },
        cardTypeSet: (state, {payload}: PayloadAction<CardType>) => {
            state.cardType = payload;
        },
        paymentOptionsSet: (
            state,
            {payload}: PayloadAction<PaymentOptions>
        ) => {
            state.paymentOptions = payload;
        },
        defaultPaymentMethodSet: (
            state,
            {payload}: PayloadAction<PaymentMethodType>
        ) => {
            state.defaultPaymentMethod = payload;
        },
        paymentMethodSet: (
            state,
            {payload}: PayloadAction<PaymentMethodType>
        ) => {
            state.paymentMethod = payload;
        },
        errorsSet: (state, {payload}: PayloadAction<string[]>) => {
            state.errors = payload;
        },
        errorSet: (state, {payload}: PayloadAction<string>) => {
            state.errors.push(payload);
        },
        connectedAccountIdSet: (state, {payload}: PayloadAction<string>) => {
            state.connectedAccountId = payload;
        },
        paymentIntentIdSet: (state, {payload}: PayloadAction<string>) => {
            state.paymentIntentId = payload;
        },
        isCardVerifiedSet: (state, {payload}: PayloadAction<boolean>) => {
            state.isCardVerified = payload;
        },
        isLoadingPaymentInfoSet: (state, {payload}: PayloadAction<boolean>) => {
            state.isLoadingPaymentInfo = payload;
        },
        confirmationTokenSet: (state, {payload}: PayloadAction<string>) => {
            state.confirmationToken = payload;
        },
        gatewayAvailableSet: (state, {payload}: PayloadAction<boolean>) => {
            state.gatwayAvailable = payload;
        },
        invoiceAvailableSet: (state, {payload}: PayloadAction<boolean>) => {
            state.invoiceAvailable = payload;
        },
        paymentTypeSet: (state, {payload}: PayloadAction<PaymentType>) => {
            state.paymentType = payload;
        },
        removedBenchIdsSet: (state, {payload}: PayloadAction<number>) => {
            if (!state.removedBenchIds.includes(payload)) {
                state.removedBenchIds?.push(payload);
            }
        },
        removedBenchIdsUnset: (state, {payload}: PayloadAction<number>) => {
            state.removedBenchIds = state.removedBenchIds?.filter(
                (id) => id !== payload
            );
        },
        removedJobCabinetIdsSet: (state, {payload}: PayloadAction<number>) => {
            state.removedJobCabinetIds?.push(payload);
        },
        removedJobCabinetIdsUnset: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            state.removedJobCabinetIds = state.removedJobCabinetIds?.filter(
                (id) => id !== payload
            );
        },
        removedExtraVariationIdsSet: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            state.removedExtraVariationIds?.push(payload);
        },
        removedExtraVariationIdsUnset: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            state.removedExtraVariationIds =
                state.removedExtraVariationIds?.filter((id) => id !== payload);
        },
        totalRemovedVariationCostSet: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            state.totalRemovedVariationCost += payload;
        },
        totalRemovedVariationCostReset: (state) => {
            state.totalRemovedVariationCost = 0;
            state.totalGSTRemovedWithVariationCost = 0;
        },
        clearState: (state) => {
            state.previousPaymentMethod = undefined;
        },
    },
    selectors: {
        selectRemovedBenchIds: (state) => state.removedBenchIds,
        selectRemovedJobCabinetIds: (state) => state.removedJobCabinetIds,
        selectTotalRemovedVariationCost: (state) =>
            state.totalRemovedVariationCost,
        selectRemovedExtraVariationIds: (state) =>
            state.removedExtraVariationIds,
        selectPaymentType: (state) => state.paymentType,
        selectGatewayAvailable: (state) => state.gatwayAvailable,
        selectInvoiceAvailable: (state) => state.invoiceAvailable,
        selectConfirmationToken: (state) => state.confirmationToken,
        selectIsLoadingPaymentInfo: (state) => state.isLoadingPaymentInfo,
        selectIsCardVerified: (state) => state.isCardVerified,
        selectPaymentIntentId: (state) => state.paymentIntentId,
        selectConnectedAccountId: (state) => state.connectedAccountId,
        selectErrors: (state) => state.errors,
        selectPaymentMethod: (state) => state.paymentMethod,
        selectPaymentOptions: (state) => state.paymentOptions,
        selectCardType: (state) => state.cardType,
        selectClientSecret: (state) => state.clientSecret,
        selectUpdatingSession: (state) => state.updatingSession,
        selectDefaultPaymentMethod: (state) => state.defaultPaymentMethod,
        selectAccountPayAvailable: (state) => state.accountPayAvailable,
        selectTotalRemovedVariationCostGST: (state) =>
            state.totalGSTRemovedWithVariationCost,
        selectNewJobTotal: (state) => state.newJobTotal,
        selectAmountPaid: (state) => state.amountPaid,
        selectIsLoadingMisc: (state) => state.isLoadingMisc,
        selectHasCustomerOverpaid: (state) => state.overPaid,
        selectFullAmount: (state) => state.fullAmount,
        selectProcessingPayment: (state) => state.processingPayment,
        selectPreviousPaymentMethod: (state) => state.previousPaymentMethod,
        selectJobDetails: (state) => state.jobDetails,
    },
});

export const {
    removedBenchIdsSet,
    removedBenchIdsUnset,
    removedJobCabinetIdsSet,
    removedJobCabinetIdsUnset,
    removedExtraVariationIdsSet,
    removedExtraVariationIdsUnset,
    totalRemovedVariationCostSet,
    totalRemovedVariationCostReset,
    paymentTypeSet,
    invoiceAvailableSet,
    gatewayAvailableSet,
    confirmationTokenSet,
    isLoadingPaymentInfoSet,
    isCardVerifiedSet,
    paymentIntentIdSet,
    connectedAccountIdSet,
    errorSet,
    errorsSet,
    paymentMethodSet,
    paymentOptionsSet,
    cardTypeSet,
    clientSecretSet,
    updatingSessionSet,
    defaultPaymentMethodSet,
    accountPayAvailableSet,
    removedVariationGSTSet,
    miscDataSet,
    isLoadingMiscSet,
    fullAmountSet,
    processingPaymentSet,
    previousPaymentMethodSet,
    clearState,
    jobDetailsSet,
} = confirmationSlice.actions;

export const {
    selectRemovedBenchIds,
    selectRemovedJobCabinetIds,
    selectTotalRemovedVariationCost,
    selectRemovedExtraVariationIds,
    selectPaymentType,
    selectGatewayAvailable,
    selectInvoiceAvailable,
    selectConfirmationToken,
    selectIsLoadingPaymentInfo,
    selectIsCardVerified,
    selectPaymentIntentId,
    selectConnectedAccountId,
    selectErrors,
    selectPaymentMethod,
    selectPaymentOptions,
    selectCardType,
    selectClientSecret,
    selectUpdatingSession,
    selectDefaultPaymentMethod,
    selectAccountPayAvailable,
    selectTotalRemovedVariationCostGST,
    selectNewJobTotal,
    selectAmountPaid,
    selectIsLoadingMisc,
    selectHasCustomerOverpaid,
    selectFullAmount,
    selectProcessingPayment,
    selectPreviousPaymentMethod,
    selectJobDetails,
} = confirmationSlice.selectors;

export default confirmationSlice.reducer;
